import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import COLORS from '../../../constants/colors';
import { useSearchParamsState } from '../../../hooks';
import { useAppDispatch } from '../../../redux/hooks';
import {
  useCreatePlanRulesGroupMutation,
  useFetchPlanRulesQuery,
  usePublishPlanRulesMutation,
} from '../../../redux/features/planRules/planRules.apiSlice';
import { fetchProductTypes } from '../../../redux/modules/productType/product-type.actions';
import { productTypeSelector } from '../../../redux/modules/productType/product-type.selectors';
import {
  GroupRuleValues,
  PlanRulesDraft,
  PlanRulesGroup,
  PlanRulesPlan,
  PlanRulesView,
} from '../../../types/planRules';
import { PUBLISH_ERROR_MESSAGES } from './premium.constants';
import { buildChanges, buildPlanWarnings, buildPublishRequest, ruleKey, toDraft } from './premium.helpers';
import { DEFAULT_PREMIUM_SECTION, PremiumSection, premiumTabSections } from './premiumTabs';
import { ChangeHistory } from './ChangeHistory';
import { ComparePlans } from './ComparePlans';
import { NewGroupDialog } from './NewGroupDialog';
import { PlanTotals } from './PlanTotals';
import { PremiumServices } from './PremiumServices';
import { ProductGroups } from './ProductGroups';
import { ReviewDialog } from './ReviewDialog';
import { RoboAdvice } from './RoboAdvice';
import { UnassignedProducts } from './UnassignedProducts';

/** Enough to load every product type for names and the "products without a group" check. */
const PRODUCT_TYPES_PAGE = { limit: 1000, offset: 0 };

const errorCode = (error: unknown) => (error as { data?: { message?: string } })?.data?.message;

/**
 * Settings → Premium (doc/plan-limits/admin-ui-design.md). Every edit goes into a draft; nothing is live until
 * "Review & publish", which sends all changes in one request. Applies live to every user after publish.
 */
export function PremiumTab() {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [section, setSection] = useSearchParamsState('premiumTab', DEFAULT_PREMIUM_SECTION);
  const { data: view, isFetching, error, refetch } = useFetchPlanRulesQuery();
  const { productTypes } = useSelector(productTypeSelector);
  const [publishPlanRules, { isLoading: publishing }] = usePublishPlanRulesMutation();
  const [createPlanRulesGroup, { isLoading: creatingGroup }] = useCreatePlanRulesGroupMutation();

  const [loaded, setLoaded] = useState<PlanRulesView | null>(null);
  const [saved, setSaved] = useState<PlanRulesDraft | null>(null);
  const [draft, setDraft] = useState<PlanRulesDraft | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchProductTypes(PRODUCT_TYPES_PAGE));
  }, []);

  const products = useMemo(
    () =>
      (productTypes || [])
        .filter((productType) => productType.id !== undefined)
        .map((productType) => ({ id: Number(productType.id), name: productType.nameEn })),
    [productTypes],
  );
  const productName = (id: number) => products.find((product) => product.id === id)?.name ?? `Product #${id}`;

  const changes = useMemo(
    () => (saved && draft && loaded ? buildChanges(saved, draft, loaded.services, productName) : []),
    [saved, draft, loaded, products],
  );
  const changedKeys = useMemo(() => new Set(changes.map((change) => change.key)), [changes]);

  // Load the rules. A reload never wipes unpublished edits.
  const applyView = (next: PlanRulesView) => {
    setLoaded(next);
    setSaved(toDraft(next));
    setDraft(toDraft(next));
  };

  useEffect(() => {
    if (view && (!loaded || !changes.length)) {
      applyView(view);
    }
  }, [view]);

  // Leaving the page with unpublished edits asks first.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (changes.length) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [changes.length]);

  if (error && !loaded) {
    return <Alert severity="error">Could not load the plan limits. {errorCode(error)}</Alert>;
  }

  if (!loaded || !saved || !draft) {
    return <LinearProgress />;
  }

  const update = (change: (current: PlanRulesDraft) => PlanRulesDraft) =>
    setDraft((current) => (current ? change(current) : current));

  const handlePlanChange = (planId: number, patch: Partial<PlanRulesPlan>) =>
    update((current) => ({
      ...current,
      plans: current.plans.map((plan) => (plan.id === planId ? { ...plan, ...patch } : plan)),
    }));

  const handleGroupChange = (groupId: number, patch: Partial<PlanRulesGroup>) =>
    update((current) => ({
      ...current,
      groups: current.groups.map((group) => (group.id === groupId ? { ...group, ...patch } : group)),
    }));

  const handleRuleChange = (planId: number, groupId: number, values: GroupRuleValues | undefined) =>
    update((current) => {
      const groupRules = { ...current.groupRules };

      if (values) {
        groupRules[ruleKey(planId, groupId)] = values;
      } else {
        delete groupRules[ruleKey(planId, groupId)];
      }

      return { ...current, groupRules };
    });

  // A product is in one group only: moving it takes it out of the others. groupId undefined = out of every group.
  const handleMoveProduct = (productTypeId: number, groupId: number | undefined) =>
    update((current) => ({
      ...current,
      groups: current.groups.map((group) => {
        const productTypeIds = group.productTypeIds.filter((id) => id !== productTypeId);

        return { ...group, productTypeIds: group.id === groupId ? [...productTypeIds, productTypeId] : productTypeIds };
      }),
    }));

  const handleServiceChange = (planId: number, serviceId: number, included: boolean) =>
    update((current) => {
      const serviceRules = { ...current.serviceRules };
      const key = ruleKey(planId, serviceId);

      if (included) {
        serviceRules[key] = saved.serviceRules[key] ?? { limit: null };
      } else {
        delete serviceRules[key];
      }

      return { ...current, serviceRules };
    });

  const handleOpen = (next: PremiumSection, groupId?: number) => {
    if (groupId) {
      setSelectedGroupId(groupId);
    }

    setSection(next);
  };

  const handlePublish = async (note: string) => {
    try {
      const next = await publishPlanRules(
        buildPublishRequest(saved, draft, loaded.services, loaded.version, note),
      ).unwrap();

      applyView(next);
      setReviewOpen(false);
      enqueueSnackbar(`Published ${changes.length} change${changes.length === 1 ? '' : 's'}. Live for all users now.`, {
        variant: 'success',
      });
    } catch (publishError) {
      const code = errorCode(publishError);

      const knownMessage = code ? PUBLISH_ERROR_MESSAGES[code] : undefined;

      enqueueSnackbar(
        code === 'PLAN_RULES_CHANGED'
          ? 'Someone else published changes. Discard yours and reload to see them, then edit again.'
          : knownMessage ?? `Not published. Nothing changed for users.${code ? ` (${code})` : ''}`,
        { variant: code === 'PLAN_RULES_CHANGED' ? 'warning' : 'error' },
      );
    }
  };

  const handleDiscard = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`Discard ${changes.length} unpublished change${changes.length === 1 ? '' : 's'}?`)) {
      // The newest rules we have (someone else may have published meanwhile), then ask the server again.
      applyView(view ?? loaded);
      refetch();
    }
  };

  const handleCreateGroup = async (name: string, key: string) => {
    try {
      const next = await createPlanRulesGroup({ name, key }).unwrap();

      applyView(next);
      setSelectedGroupId(next.groups.find((group) => group.key === key)?.id);
      setNewGroupOpen(false);
      enqueueSnackbar(`Group "${name}" created. Add products and rules, then publish.`, { variant: 'success' });
    } catch (createError) {
      const code = errorCode(createError);

      enqueueSnackbar(
        code === 'GROUP_KEY_EXISTS' ? 'A group with this name already exists.' : `Not created. ${code ?? ''}`,
        {
          variant: 'error',
        },
      );
    }
  };

  const linked = new Set(draft.groups.flatMap((group) => group.productTypeIds));
  const unassigned = products.filter((product) => !linked.has(product.id));
  const tabs = premiumTabSections
    .flatMap((tabSection) => tabSection.tabs)
    // The count shows how many products are open to everyone.
    .map((tab) => {
      if (tab.value !== 'unassigned' || !unassigned.length) {
        return tab;
      }

      return { ...tab, label: `${tab.label} (${unassigned.length})` };
    });

  const renderSection = () => {
    switch (section as PremiumSection) {
      case 'totals':
        return <PlanTotals draft={draft} onPlanChange={handlePlanChange} />;
      case 'robo':
        return <RoboAdvice draft={draft} onPlanChange={handlePlanChange} />;
      case 'groups':
        return (
          <ProductGroups
            draft={draft}
            productTypes={products}
            selectedGroupId={selectedGroupId}
            onSelectGroup={setSelectedGroupId}
            onGroupChange={handleGroupChange}
            onRuleChange={handleRuleChange}
            onMoveProduct={handleMoveProduct}
            canCreateGroup={!changes.length}
            onCreateGroup={() => setNewGroupOpen(true)}
          />
        );
      case 'services':
        return <PremiumServices draft={draft} services={loaded.services} onServiceChange={handleServiceChange} />;
      case 'unassigned':
        return <UnassignedProducts products={unassigned} groups={draft.groups} onMoveProduct={handleMoveProduct} />;
      case 'history':
        return <ChangeHistory view={loaded} productName={productName} />;
      default:
        return (
          <ComparePlans
            draft={draft}
            services={loaded.services}
            productName={productName}
            changedKeys={changedKeys}
            onOpen={handleOpen}
          />
        );
    }
  };

  return (
    <Box sx={{ pb: changes.length ? '88px' : 0 }}>
      {isFetching && <LinearProgress sx={{ mb: '8px' }} />}
      {/* Tabs on top, not a side menu: the tables need the full width. */}
      <Tabs
        value={section}
        onChange={(event, next: string) => setSection(next)}
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
        aria-label="Premium sections"
        sx={{ mb: '16px', borderBottom: `1px solid ${alpha(COLORS.MAIN_DARK, 0.08)}` }}
      >
        {tabs.map(({ value, label, IconComponent }) => (
          <Tab
            key={value}
            value={value}
            label={label}
            icon={<IconComponent fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: 'capitalize', minHeight: '48px' }}
            disableRipple
          />
        ))}
      </Tabs>
      {renderSection()}

      {changes.length > 0 && (
        <Paper
          elevation={6}
          role="region"
          aria-label="Unpublished changes"
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            px: '24px',
            py: '12px',
            borderRadius: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS.ORANGE }} />
          <Typography sx={{ fontWeight: 700 }}>
            {changes.length} unpublished change{changes.length === 1 ? '' : 's'}
          </Typography>
          <Typography sx={{ color: COLORS.LIGHT_GRAY }}>Not live yet. Users still see the old limits.</Typography>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleDiscard}>Discard</Button>
          <Button variant="contained" onClick={() => setReviewOpen(true)}>
            Review &amp; publish
          </Button>
        </Paper>
      )}

      <ReviewDialog
        open={reviewOpen}
        changes={changes}
        extraWarnings={buildPlanWarnings(draft)}
        publishing={publishing}
        onCancel={() => setReviewOpen(false)}
        onPublish={handlePublish}
      />
      <NewGroupDialog
        open={newGroupOpen}
        creating={creatingGroup}
        onCancel={() => setNewGroupOpen(false)}
        onCreate={handleCreateGroup}
      />
    </Box>
  );
}
